import { Component, Input, OnInit } from '@angular/core';
import { groupBy } from 'lodash';
import {
    FilterInfo,
    categoryEnumToNameKeyMap,
    FilterType,
    XFilterType,
    FilterCategory
} from '../../core-data-model/FilterInfo.model';
import { Table } from '../../core-services/Tables/Table';
import {
    DropdownListEntry,
    DropdownEntryType
} from '../../core-ui/Dropdown/Dropdown.component';
import { TranslationService } from '../../translations';

@Component({
    selector: 'filter-options-list',
    template: `
        @if (tableData.possibleFilters) {
            <div class="filterOptionsList">
                @if (categorizedFilters2.length !== 0) {
                    <pi-dropdown
                        class="filterOptionsList_dropdown pi-dropdown--noCaret"
                        placeholderIcon="fa-filter color-success"
                        [placeholder]="TR.key('common.filters')"
                        [list]="categorizedFilters2"
                        (onSelected)="addFilterPill($event)"
                        data-testing="openFilterDropdown"
                    >
                        <ng-template let-item="item">
                            @if (item.type === FilterType.ReferenceProfile) {
                                <ok-checkbox
                                    class="dontBold"
                                    style="display:inline-block"
                                    [label]="TR.key(item.getDisplayString())"
                                    [ngModel]="filterActive(item)"
                                    (ngModelChange)="toggleFilter(item)"
                                >
                                </ok-checkbox>
                            }
                            @if (item.type !== FilterType.ReferenceProfile) {
                                {{ item.getDisplayString() | translate }}
                            }
                        </ng-template>
                    </pi-dropdown>
                } @else {
                    <div class="filterOptionsList_allFiltersNotice">
                        <i class="fa fa-filter"></i>
                        {{ 'common.filters' | translate }}
                    </div>
                }
                <div class="filterOptionsList_pillList">
                    @for (
                        filterPill of filterPillCollection;
                        track filterPill
                    ) {
                        <ok-pill
                            [item]="filterPill"
                            (onRemove)="removeFilterPill(filterPill)"
                            [removable]="true"
                        >
                            <ng-template let-item="item">
                                {{ item.displayString }}
                            </ng-template>
                        </ok-pill>
                    }
                </div>
            </div>
        }
    `,
    standalone: false
})
export class FilterOptionsListComponent implements OnInit {
    FilterType = FilterType;
    @Input()
    tableData: Table<any>;
    filterPillCollection: FilterPill[];

    categorizedFilters2: DropdownListEntry[];

    constructor(public TR: TranslationService) {}
    ngOnInit(): void {
        this.categorizedFilters2 = this.categorizedFilters();
    }

    toggleFilter(filter: FilterInfo) {
        if (this.filterActive(filter)) {
            this.tableData.removeFilter(filter);
        } else {
            this.tableData.addFilter(filter);
        }
        this.transformToPills(); // update the filterPillCcollection
    }

    filterActive(filterToCheck: FilterInfo) {
        return !!this.tableData.searchState.filterState.find(
            (filter) =>
                filter.type === filterToCheck.type &&
                filter.parameters === filterToCheck.parameters
        );
    }

    private trimmedFilterState(): FilterInfo[] {
        return this.tableData.searchState.filterState.filter((currentFilter) =>
            // NOTE: Don't try to use `includes` for this check because the
            // actual FilterInfo items in each list aren't the same references
            // (for some reason...)
            this.tableData.possibleFilters.some(
                (pf) => pf.type === currentFilter.type
            )
        );
    }

    private trimmedPossibleFilters(): FilterInfo[] {
        const trimmedList = this.tableData.possibleFilters
            // Remove filters that should not be shown
            .filter((f) => !f.showFilter || f.showFilter())
            .filter(
                (possibleFilter) =>
                    possibleFilter.type === FilterType.ReferenceProfile ||
                    !this.trimmedFilterState().includes(possibleFilter)
            );
        trimmedList.forEach((filter) => {
            if (filter.type === FilterType.ReferenceProfile) {
                filter.managesOwnClick = true;
            }
        });
        return trimmedList;
    }

    /* Some notes on the following code:
     * Each Filter can have a "parent node" which is a "structural node" that
     * represents a category or an entry that opens a submenu.
     * To have the filters in some sort of structure you have a couple options.
     * 1) You can build the structure explicitly by defining it with some objects
     * 2) You can build the structure implicitly by putting a property on the fiters
     *    and dynamically constructing the structure.
     * The problem with the second approach is that it is hard to build more complex
     * structures. Since our use case is fairly straight foward I am going to stick
     * with the second approach and state the rule is that filters can have a category
     * and a submenu. Submenu are placed under categories.
     */
    private submenuizeFilters() {
        const entryList: any[] = [];
        const grouped = groupBy(
            this.trimmedPossibleFilters(),
            (item) => item.type.id
        );
        for (const filterTypeName in grouped) {
            const filterType = FilterType[filterTypeName];
            if (grouped[filterTypeName].length > 1) {
                const menuEntry = this.getCategoryItem(
                    `filter${filterTypeName}`
                );
                menuEntry.entryType = DropdownEntryType.SubMenu;
                menuEntry.children = grouped[filterTypeName];
                menuEntry.category = grouped[filterTypeName][0].type.category;
                menuEntry.title = this.TR.key(filterType.getDisplayString());
                entryList.push(menuEntry);
            } else {
                entryList.push(grouped[filterTypeName][0]);
            }
        }
        return entryList;
    }

    categorizedFilters(): DropdownListEntry[] {
        const categoryList: DropdownListEntry[] = [];
        const grouped = groupBy(
            this.submenuizeFilters(),
            (item) => item.category || item.type.category
        );
        for (const category in grouped) {
            const item = this.getCategoryItem(category);
            item.entryType = DropdownEntryType.Category;
            item.children = grouped[category];
            categoryList.push(item);
        }
        return categoryList;
    }

    // This is a hack to get around a chrome/angular bug
    // Trackby might also get around it
    categoryStore: {
        [key: string]: {
            title: string;
            category?: FilterCategory;
            entryType?: DropdownEntryType;
            children?: DropdownListEntry[];
        };
    } = {};

    private getCategoryItem(category: string) {
        if (!this.categoryStore[category]) {
            this.categoryStore[category] = {
                title:
                    category === 'undefined'
                        ? null
                        : this.TR.key(
                              categoryEnumToNameKeyMap[parseInt(category, 10)]
                          )
            };
        }
        return this.categoryStore[category];
    }

    transformToPills() {
        const listOfListsOfFilters = Object.values(
            groupBy(this.trimmedFilterState(), (filter) => filter.type.id)
        );
        this.filterPillCollection = listOfListsOfFilters.map(
            (listOfFiltersSharingType) => ({
                displayString: listOfFiltersSharingType
                    .map((f) => this.TR.key(f.getDisplayString()))
                    .join(' + '),
                filterTypeToRemove: listOfFiltersSharingType[0].type
            })
        );
    }

    removeFilterPill(pill: FilterPill) {
        this.tableData.removeFiltersOfType(pill.filterTypeToRemove);
        this.transformToPills(); // update the filterPillCcollection
    }

    addFilterPill(filter: FilterInfo) {
        this.tableData.addFilter(filter);
        this.transformToPills(); // update the filterPillCcollection
    }
}

type FilterPill = {
    displayString: string;
    filterTypeToRemove: XFilterType;
};
